import React, { useRef, useState } from 'react';
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath, useReactFlow } from '@xyflow/react';
import { getEditableRoutePoints, routePointsToPath } from './edgeRouting';

export default function EditableOrganizationEdge(props) {
    const { id, data, style, markerStart, markerEnd } = props;
    const { screenToFlowPosition } = useReactFlow();
    const drag = useRef(null);
    const [dragPoint, setDragPoint] = useState(null);
    // Turn below the parent so edges skipping a row do not cross intermediate nodes.
    const centerY = props.sourcePosition === 'bottom' && props.targetPosition === 'top' && props.targetY > props.sourceY
        ? props.sourceY + Math.min(24, (props.targetY - props.sourceY) / 2)
        : undefined;
    const [defaultPath, labelX, labelY] = getSmoothStepPath({ ...props, centerY, borderRadius: 0, offset: 16 });
    const routePoint = dragPoint || data?.routePoint;
    const point = routePoint || { x: labelX, y: labelY };
    const path = routePoint ? routePointsToPath(getEditableRoutePoints(props, routePoint)) : defaultPath;

    const pointerPosition = event => screenToFlowPosition({ x: event.clientX, y: event.clientY });

    const finishDrag = event => {
        if (drag.current?.pointerId !== event.pointerId) return;
        if (drag.current.moved) data.onRouteChange(id, drag.current.point);
        drag.current = null;
        setDragPoint(null);
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    };

    return (
        <>
            <BaseEdge id={id} path={path} style={style} markerStart={markerStart} markerEnd={markerEnd} />
            <EdgeLabelRenderer>
                <button type="button" className="organization-chart-route-handle nodrag nopan"
                    style={{ transform: `translate(-50%, -50%) translate(${point.x}px, ${point.y}px)` }}
                    aria-label={`Move connector ${id}`} title="Drag to move the line. Arrow keys: move. Double-click: reset."
                    onPointerDown={event => {
                        if (event.button !== 0) return;
                        event.stopPropagation();
                        const start = pointerPosition(event);
                        drag.current = { pointerId: event.pointerId, start, origin: point, point, moved: false };
                        event.currentTarget.setPointerCapture(event.pointerId);
                    }}
                    onPointerMove={event => {
                        if (drag.current?.pointerId !== event.pointerId) return;
                        const current = pointerPosition(event);
                        const next = { x: drag.current.origin.x + current.x - drag.current.start.x,
                            y: drag.current.origin.y + current.y - drag.current.start.y };
                        drag.current.point = next;
                        drag.current.moved = true;
                        setDragPoint(next);
                    }}
                    onPointerUp={finishDrag}
                    onPointerCancel={() => { drag.current = null; setDragPoint(null); }}
                    onLostPointerCapture={() => { drag.current = null; setDragPoint(null); }}
                    onDoubleClick={event => { event.stopPropagation(); data.onRouteChange(id, null); }}
                    onKeyDown={event => {
                        const steps = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
                        const step = steps[event.key];
                        if (!step) return;
                        event.preventDefault();
                        event.stopPropagation();
                        const distance = event.shiftKey ? 20 : 5;
                        data.onRouteChange(id, { x: point.x + step[0] * distance, y: point.y + step[1] * distance });
                    }}
                >
                    <span aria-hidden="true" />
                </button>
            </EdgeLabelRenderer>
        </>
    );
}
